/* eslint-disable @typescript-eslint/no-explicit-any */
import CategoriesListing from "@/components/organisms/categories-listing";
import ProductListing from "@/components/organisms/products-listing";
import { resolveAlternates } from "@/helpers/seo";
import { CategorieDocumentWithChildren, resolveCategoriesTree } from "@/lib/prismic/categories";
import { generateSEO } from "@/helpers/seo";
import { getData } from "@/lib/prismic/data";
import { notFound } from "next/navigation";
import { use } from "react";

const Category = ({ resolution }: TemplateProps) => {
  // get last part of the "slugs" array
  const categoryHandle = resolution?.slugs[resolution.slugs.length - 1];

  // Get the data for the category from Prismic
  const data = use(
    getData({
      ...resolution,
      templateName: "categorie",
      slugs: [categoryHandle],
    })
  );

  // resolve alternates for current category (usefull for instantsearch in product listing)
  const tree = use(resolveAlternates(data, false));

  // If the tree is empty or has only one element, we need to resolve the categories tree
  // to find the category with the matching handle.

  const categoriesTree = use(resolveCategoriesTree(resolution.locale));

  let defaultCategoriesTree = categoriesTree;
  if (resolution.locale !== "fr") {
    defaultCategoriesTree = use(resolveCategoriesTree("fr"));
  }

  // find the category with the matching handle
  const category = categoriesTree.find((cat: any) => cat.uid === categoryHandle);

  // If no data is found, return a 404 page
  if (!data?.data) {
    return notFound();
  }

  // Now, we'll check the category structure.
  if (
    resolution.slugs.length > 1 &&
    data.data.parentCategorie &&
    data.data.parentCategorie.uid !== resolution.slugs[resolution.slugs.length - 2]
  ) {
    return notFound();
  }

  // If we're not in main language (fr), loop through defaultCategory.alternates to find current category.id
  const defaultCategory = defaultCategoriesTree.find((cat: any) => {
    return cat.alternate_languages?.some((alt: any) => alt.id === category?.id);
  });

  const props = {
    categoryData: {
      title: data.data.title,
      longDescription: data.data.longDescription,
    },
    seo: {
      title: data.data.meta_title,
      description: data.data.meta_description,
    },
    categoryTree: tree,
    locale: resolution.locale,
    categoryHandle: categoryHandle,
    slugs: resolution.slugs,
    category: category,
    defaultCategory: defaultCategory,
    navCategories: categoriesTree,
    currentLocale: resolution.locale,
  };

  let updatedCategory: any = {};

  // Loop all "category.children" to assign featuredImage from "defaultCategorieTree" if we're not on main language
  if (resolution.locale !== "fr-fr") {
    updatedCategory = {
      ...category,
      children: category?.children?.map((child: any) => {
        // Find child alternate in defaultCategory
        const childAlternate = defaultCategory?.children?.find((c: any) => {
          return c.alternate_languages?.some((alt: any) => alt.id === child.id);
        });

        if (childAlternate) {
          child.data.display = childAlternate.data.display;
          child.data.featuredImage = childAlternate.data.featuredImage;
        }

        return child;
      }),
    } as CategorieDocumentWithChildren;

    const updatedNavCategories: any[] = props.navCategories.map((navCat: any) => {
      const navCatAlternate = defaultCategoriesTree?.find((c: any) => {
        return c.alternate_languages?.some((alt: any) => alt.id == navCat.id);
      });

      if (navCatAlternate) {
        navCat.data.display = navCatAlternate.data.display;
        navCat.data.featuredImage = navCatAlternate.data.featuredImage;
      }

      if (navCat.children && navCat.children.length > 0) {
        navCat.children = navCat.children.map((child: any) => {
          const childAlternate = navCatAlternate?.children?.find((c: any) => {
            return c.alternate_languages?.some((alt: any) => alt.id == child.id);
          });

          if (childAlternate) {
            child.data.display = childAlternate.data.display;
            child.data.featuredImage = childAlternate.data.featuredImage;
          }

          if (child.children && child.children.length > 0) {
            child.children = child.children.map((subChild: any) => {
              const subChildAlternate = childAlternate?.children?.find((c: any) => {
                return c.alternate_languages?.some((alt: any) => alt.id == subChild.id);
              });

              if (subChildAlternate) {
                subChild.data.display = subChildAlternate.data.display;
                subChild.data.featuredImage = subChildAlternate.data.featuredImage;
              }

              return subChild;
            });
          }

          return child;
        });
      }

      return navCat;
    });

    props.navCategories = updatedNavCategories;
  }

  if (tree.length === 1 && category?.children?.length === 0) {
    return (
      <>
        <CategoriesListing {...props} category={updatedCategory || category} />
        {/* Render the ProductListing component if there's only one category */}
        <ProductListing {...props} hasFilter={false} />
      </>
    );
  }

  return tree.length > 1 ? (
    <ProductListing {...props} />
  ) : (
    <CategoriesListing {...props} category={updatedCategory || category} />
  );
};

export default Category;

Category.generateMetadata = async (resolution: any) => {
  // get last part of the "slugs" array
  const categoryHandle = resolution?.slugs[resolution.slugs.length - 1];

  // Get the data for the category from Prismic

  const data = await getData({
    ...resolution,
    templateName: "categorie",
    slugs: [categoryHandle],
  });

  if (!data?.data) {
    return {};
  }

  return await generateSEO(data, resolution.locale, false, true);
};
